/**
 * Base Widget Preview Utilities
 *
 * Generic utilities for widget iframe previews.
 * Widget-specific composers should import and use these.
 */

import { sendToWidget, PARENT_TO_WIDGET } from '@/js/widget/widget-messaging.js';

/**
 * Build widget iframe source URL
 * @param {Object} config - Configuration
 * @param {string} config.widget_name - Widget name (e.g., 'guest-check-in', 'contact-form'). Corresponds to test html file name
 * @param {string} config.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} config.is_development - Whether in development mode
 * @param {number} config.dev_port - Development server port (default: 5175)
 * @returns {string} Widget iframe source URL
 *
 * @example
 * buildWidgetIframeSrc({
 *   widget_name: 'guest-check-in',
 *   preview_mode: 'mobile',
 *   is_development: true
 * })
 */
export function buildWidgetIframeSrc(config) {
  const {
    widget_name,
    preview_mode = 'tablet-landscape',
    is_development = false,
    dev_port = 5175,
  } = config;
  
  const is_staging = window.location.hostname.includes('staging');

  let widget_file_name = widget_name;
  if(is_staging) {
    widget_file_name = `staging-${widget_name}`;
  }

  const base_url = is_development
    ? `http://localhost:${dev_port}/`
    : `https://files.redchirp.com/v1/prod/widget/${widget_file_name}.html`;

  const mobile_param = preview_mode === 'mobile' ? '?mobile=true' : '';

  return `${base_url}${mobile_param}`;
}

/**
 * Send settings to widget iframe and ensure it's shown
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {Object} settings - Settings to send to widget
 */
export function sendSettingsToWidget(iframe, settings) {
  sendToWidget(iframe, PARENT_TO_WIDGET.SET_SETTINGS, settings);
  sendToWidget(iframe, PARENT_TO_WIDGET.SHOW);
}

/**
 * Send page change message to widget iframe
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {string} page - Page to navigate to
 */
export function sendPageChangeToWidget(iframe, page) {
  sendToWidget(iframe, PARENT_TO_WIDGET.NAVIGATE_TO_PAGE, page);
}

/**
 * Send auto-fill test data message to widget
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {Object} test_data - Test data to auto-fill
 */
export function sendAutoFillTestData(iframe, test_data) {
  sendToWidget(iframe, PARENT_TO_WIDGET.AUTO_FILL_TEST_DATA, test_data);
}

/**
 * Base settings structure for widget previews
 * @param {Object} config - Configuration
 * @param {string} config.form_id_key - Key name for form ID (e.g., 'guest_check_in_form_id')
 * @param {string} config.form_id - Form ID (real or preview ID)
 * @param {Object} config.form - Form data object
 * @param {boolean} config.is_test_submission - Whether this is a test submission
 * @param {boolean} config.is_template_preview - Whether this is a template preview
 * @param {string} config.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @returns {Object} Base settings structure
 */
export function createBaseWidgetSettings(config) {
  const {
    form_id_key = 'form_id',
    form_id,
    form,
    is_test_submission = true,
    is_template_preview = false,
    preview_mode = 'tablet-landscape',
  } = config;

  const settings = {
    is_test_submission,
    preview_mode,
    is_template_preview,
    mobile_breakpoint: preview_mode === 'mobile' ? 0 : 600,
    form,
  };

  // Set form ID using widget-specific key
  settings[form_id_key] = form_id || `preview-${Date.now()}`;

  return settings;
}

/**
 * Apply user customizations over template defaults
 * Ensures user selections (images, theme) take precedence over template values
 *
 * @param {Object} base_form - Base form with template defaults
 * @param {Object} customizations - User customizations
 * @param {string} customizations.background_image_url - Background image URL
 * @param {string} customizations.side_image_url - Side image URL
 * @param {string} customizations.logo_url - Logo URL
 * @param {boolean} customizations.use_logo - Whether to show logo
 * @param {Object} customizations.comp_custom_fonts - Custom fonts data
 * @param {Object} customizations.theme - Theme object
 * @returns {Object} Form with customizations applied
 */
export function applyUserCustomizations(base_form, customizations = {}) {
  const { background_image_url, side_image_url, logo_url, use_logo, comp_custom_fonts, theme } = customizations;
  const customized_form = { ...base_form };

  if (background_image_url) {
    customized_form.page_background = {
      url: background_image_url,
      style: customized_form.page_background?.style || '',
    };
  }

  if (side_image_url) {
    customized_form.side_image = {
      url: side_image_url,
      style: customized_form.side_image?.style || '',
    };
  }

  if (use_logo && logo_url) {
    customized_form.use_logo = true;
    customized_form.logo_image = {
      url: logo_url,
      style: customized_form.logo_image?.style || '',
    };
  } else if (use_logo === false) {
    customized_form.use_logo = false;
  }

  if (comp_custom_fonts) {
    customized_form.comp_custom_fonts = comp_custom_fonts;
  }

  if (theme) {
    customized_form.theme = { ...customized_form.theme, ...theme };
  }

  return customized_form;
}
